import { instantToLocalParts, localPartsToInstant } from '../../Utils/eventTime';

// Pure conversion between an event row (+ Defaults) and the EventSettings
// form shape, and back to the API payload. Router-free on purpose: Event.jsx
// imports react-router-dom (useNavigate/useParams), which currently can't be
// resolved under this repo's Jest config (see eventCardLabels.js for the
// same, already-diagnosed pre-existing issue) — pulling the actual
// conversion logic out here is what makes it unit-testable at all.

// `eventData` (GET /admin/events/:id row) + `defaults` (GET /admin/prices,
// Defaults) -> the EventSettings `value` shape. Caller resolves a fallback
// timezone onto `eventData.timezone` before calling this (an event created
// before SCRUM-334 has none) — this function only ever reads
// `eventData.timezone` as given.
export function eventToForm(eventData, defaults = {}) {
  const timezone = eventData.timezone;
  const registration = eventData.registrationStartsAt
    ? instantToLocalParts(eventData.registrationStartsAt, timezone)
    : { ymd: '', hm: '' };
  const start = eventData.startsAt ? instantToLocalParts(eventData.startsAt, timezone) : { ymd: '', hm: '' };
  const end = eventData.endsAt ? instantToLocalParts(eventData.endsAt, timezone) : { ymd: '', hm: '' };
  const expires = eventData.expiresAt ? instantToLocalParts(eventData.expiresAt, timezone) : { ymd: '', hm: '' };
  return {
    timezone,
    registrationYmd: registration.ymd,
    registrationHm: registration.hm,
    startYmd: start.ymd,
    startHm: start.hm,
    endYmd: end.ymd,
    endHm: end.hm,
    expiresYmd: expires.ymd,
    expiresHm: expires.hm,
    imagePrice: eventData.imagePrice ?? '',
    albumPrice: eventData.albumPrice ?? '',
    discountPercent: eventData.discountPercent ?? '',
    matchThreshold: eventData.matchThreshold ?? '',
    salesWindowDays: defaults.expirationTime ?? '',
  };
}

// `endsAt` and `expiresAt` are both the END of the chosen minute, not its
// start: the operator picks "23:59" meaning "through 23:59", i.e.
// 23:59:59.999 — matching what `endOfLocalDay` already produces for the
// Defaults-driven Sales-close pre-fill. `startsAt` stays at the start of its
// minute (an event "starting at 08:00" means 08:00:00, not 08:00:59.999).
// Without this, an untouched edit->save round trip (load ->
// instantToLocalParts -> localPartsToInstant, with no `+59.999s`) quietly
// moves an existing event's end/sales-close 60 seconds earlier on every
// single save — caught live on WCC's stored `endsAt` during the Task 12 walk.
const END_OF_MINUTE_MS = 59_999;
const endOfChosenMinute = (ymd, hm, timezone) =>
  new Date(new Date(localPartsToInstant(ymd, hm, timezone)).getTime() + END_OF_MINUTE_MS).toISOString();

// EventSettings `form` value -> the create/update event API payload. Numbers
// via `Number()`; the four times as ISO instants via `localPartsToInstant`
// (registrationStartsAt and startsAt stay at the start of the chosen minute;
// endsAt/expiresAt are pushed to the end of it — see END_OF_MINUTE_MS above).
// Never includes `date` — the legacy field is retired from every write path.
export function buildTimingPayload(form) {
  return {
    timezone: form.timezone,
    registrationStartsAt: localPartsToInstant(form.registrationYmd, form.registrationHm, form.timezone),
    startsAt: localPartsToInstant(form.startYmd, form.startHm, form.timezone),
    endsAt: endOfChosenMinute(form.endYmd, form.endHm, form.timezone),
    expiresAt: endOfChosenMinute(form.expiresYmd, form.expiresHm, form.timezone),
    imagePrice: Number(form.imagePrice),
    albumPrice: Number(form.albumPrice),
    discountPercent: Number(form.discountPercent),
    matchThreshold: Number(form.matchThreshold),
  };
}

// SCRUM-380: the "Visible only to" field -> the API's allowedEmails array.
// Split on commas/whitespace/semicolons, trim, lowercase, drop blanks and
// duplicates. Blank input -> [] (public). The API normalises again; this just
// keeps what the operator sees on reload identical to what was saved.
export function parseAllowedEmails(text) {
  const out = [];
  for (const raw of String(text ?? '').split(/[,;\s]+/)) {
    const v = raw.trim().toLowerCase();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

// Upload whichever event images the operator picked and return the URLs to
// store. Only talks to S3 when there is a file to send: the old inline
// version asked the API for presigned URLs on EVERY save, deriving the keys
// from the stored URLs — an event with no images yet (the demo event) gave
// two empty keys, the API threw "Key must start with 'icons/' or
// 'banners/'", and every edit of that event was a 500 (2026-09-16).
// A key is reused from the stored URL when there is one (same object is
// overwritten, CloudFront-cached URL stays valid) and minted fresh otherwise.
// `deps` is injected so this stays unit-testable without the axios client.
export async function uploadEventImages(form, { getPresignedUrls, upload, newKey }) {
  let { iconUrl, eventBanner } = form;
  if (!form.iconFile && !form.bannerFile) return { iconUrl, eventBanner };

  const keyFrom = (url, folder) =>
    url ? url.split('/').slice(-2).join('/') : `${folder}/${newKey()}`;
  const { iconUrl: presignedIcon, bannerUrl: presignedBanner } = await getPresignedUrls(
    keyFrom(iconUrl, 'icons'),
    keyFrom(eventBanner, 'banners')
  );
  if (form.iconFile) {
    await upload(presignedIcon, form.iconFile);
    iconUrl = presignedIcon.split('?')[0];
  }
  if (form.bannerFile) {
    await upload(presignedBanner, form.bannerFile);
    eventBanner = presignedBanner.split('?')[0];
  }
  return { iconUrl, eventBanner };
}
